import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import {
  Activity,
  CalendarClock,
  Clock3,
  LocateFixed,
  MapPin,
  Navigation,
  RefreshCw,
  Search,
  UserRound,
  Users,
  WifiOff,
} from "lucide-react";

import api from "../lib/api";

import "leaflet/dist/leaflet.css";

const DEFAULT_CENTRE = [-32.9283, 151.7817];

const STATUS_META = {
  onsite: {
    label: "On Site",
    className: "bg-blue-100 text-blue-800 border-blue-200",
    markerClass: "cc-marker-onsite",
  },
  travelling: {
    label: "Travelling",
    className: "bg-emerald-100 text-emerald-800 border-emerald-200",
    markerClass: "cc-marker-travelling",
  },
  late: {
    label: "Late",
    className: "bg-red-100 text-red-800 border-red-200",
    markerClass: "cc-marker-late",
  },
  scheduled: {
    label: "Scheduled",
    className: "bg-amber-100 text-amber-800 border-amber-200",
    markerClass: "cc-marker-scheduled",
  },
  completed: {
    label: "Completed",
    className: "bg-slate-100 text-slate-700 border-slate-200",
    markerClass: "cc-marker-completed",
  },
  offline: {
    label: "Location Stale",
    className: "bg-zinc-100 text-zinc-700 border-zinc-200",
    markerClass: "cc-marker-offline",
  },
};

const markerStyles = `
.cc-live-marker {
  width: 34px;
  height: 34px;
  border-radius: 9999px;
  border: 3px solid white;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 13px;
  font-weight: 800;
}
.cc-marker-onsite { background: #2563eb; }
.cc-marker-travelling { background: #059669; }
.cc-marker-late { background: #dc2626; }
.cc-marker-scheduled { background: #d97706; }
.cc-marker-completed { background: #64748b; }
.cc-marker-offline { background: #71717a; }
`;

function createMarkerIcon(status, name) {
  const meta = STATUS_META[status] || STATUS_META.offline;
  const initial = String(name || "?").trim().charAt(0).toUpperCase();

  return L.divIcon({
    className: "",
    html: `<div class="cc-live-marker ${meta.markerClass}">${initial}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -17],
  });
}

function FitToMarkers({ rows }) {
  const map = useMap();

  useEffect(() => {
    const valid = rows.filter(
      (row) =>
        Number.isFinite(Number(row.latitude)) &&
        Number.isFinite(Number(row.longitude))
    );

    if (!valid.length) {
      return;
    }

    if (valid.length === 1) {
      map.setView(
        [Number(valid[0].latitude), Number(valid[0].longitude)],
        14
      );
      return;
    }

    const bounds = L.latLngBounds(
      valid.map((row) => [
        Number(row.latitude),
        Number(row.longitude),
      ])
    );

    map.fitBounds(bounds, {
      padding: [40, 40],
      maxZoom: 14,
    });
  }, [map, rows]);

  return null;
}

function formatTime(value) {
  if (!value) {
    return "—";
  }

  const raw = String(value);

  if (/^\d{1,2}:\d{2}/.test(raw)) {
    return raw.slice(0, 5);
  }

  const parsed = new Date(raw);

  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return parsed.toLocaleTimeString("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAge(seconds) {
  if (seconds === null || seconds === undefined) {
    return "Unknown";
  }

  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function StatusBadge({ status, label }) {
  const meta = STATUS_META[status] || STATUS_META.offline;

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.className}`}
    >
      {label || meta.label}
    </span>
  );
}

export default function LiveStaffMap() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadOperations = useCallback(async () => {
    try {
      setError("");

      const response = await api.get(
        "/live-location/operations?include_completed=true"
      );

      const payload = response?.data || {};
      setRows(Array.isArray(payload.locations) ? payload.locations : []);
      setSummary(payload.summary || {});
      setLastRefresh(new Date());
    } catch (requestError) {
      const message =
        requestError?.response?.data?.detail ||
        requestError?.message ||
        "Unable to load live staff locations.";

      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOperations();

    const interval = window.setInterval(loadOperations, 30000);

    return () => window.clearInterval(interval);
  }, [loadOperations]);

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return rows.filter((row) => {
      if (
        statusFilter === "active" &&
        ["completed", "offline"].includes(row.operational_status)
      ) {
        return false;
      }

      if (
        statusFilter !== "all" &&
        statusFilter !== "active" &&
        row.operational_status !== statusFilter
      ) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      return [
        row.staff_name,
        row.client_name,
        row.service_type,
        row.address,
        row.status_label,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedQuery)
        );
    });
  }, [query, rows, statusFilter]);

  const validMapRows = filteredRows.filter(
    (row) =>
      Number.isFinite(Number(row.latitude)) &&
      Number.isFinite(Number(row.longitude))
  );

  const filterOptions = [
    ["active", "Active"],
    ["all", "All"],
    ["onsite", "On Site"],
    ["travelling", "Travelling"],
    ["late", "Late"],
    ["scheduled", "Scheduled"],
    ["offline", "Stale"],
    ["completed", "Completed"],
  ];

  return (
    <div className="space-y-5" data-testid="live-staff-map-page">
      <style>{markerStyles}</style>

      <section className="rounded-2xl border bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <LocateFixed className="h-6 w-6 text-emerald-600" />
              <h1 className="text-2xl font-bold text-slate-900">
                Live Operations Map
              </h1>
            </div>

            <p className="mt-1 text-sm text-slate-500">
              Rostered visits, worker GPS, travel and attendance in one view.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {lastRefresh && (
              <span className="text-xs text-slate-500">
                Updated{" "}
                {lastRefresh.toLocaleTimeString("en-AU", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            )}

            <button
              type="button"
              onClick={loadOperations}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          {[
            ["On Site", summary.onsite || 0, Activity],
            ["Travelling", summary.travelling || 0, Navigation],
            ["Late", summary.late || 0, Clock3],
            ["Scheduled", summary.scheduled || 0, CalendarClock],
            ["Stale", summary.offline || 0, WifiOff],
            ["Total", summary.total || rows.length, Users],
          ].map(([label, value, Icon]) => (
            <div
              key={label}
              className="rounded-xl border bg-slate-50 px-4 py-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {label}
                </span>
                <Icon className="h-4 w-4 text-slate-500" />
              </div>

              <div className="mt-1 text-2xl font-bold text-slate-900">
                {value}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />

            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search worker, participant, service or address"
              className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {filterOptions.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatusFilter(value)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  statusFilter === value
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <section className="grid min-h-[650px] overflow-hidden rounded-2xl border bg-white shadow-sm xl:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="max-h-[650px] overflow-y-auto border-b xl:border-b-0 xl:border-r">
          <div className="sticky top-0 z-10 border-b bg-white px-4 py-3">
            <p className="font-semibold text-slate-900">
              {filteredRows.length} workers
            </p>

            <p className="text-xs text-slate-500">
              GPS automatically refreshes every 30 seconds
            </p>
          </div>

          {loading && !rows.length ? (
            <div className="p-8 text-center text-sm text-slate-500">
              Loading live operations…
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              No workers match the selected filters.
            </div>
          ) : (
            <div className="divide-y">
              {filteredRows.map((row) => (
                <article
                  key={`${row.user_id}-${row.visit_id || "none"}`}
                  className="space-y-3 p-4 hover:bg-slate-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <UserRound className="h-4 w-4 shrink-0 text-slate-500" />

                        <h3 className="truncate font-semibold text-slate-900">
                          {row.staff_name}
                        </h3>
                      </div>

                      <p className="mt-1 truncate text-sm text-slate-600">
                        {row.client_name}
                      </p>
                    </div>

                    <StatusBadge
                      status={row.operational_status}
                      label={row.status_label}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div>
                      <span className="block text-slate-400">
                        Roster
                      </span>
                      <span className="font-medium">
                        {formatTime(row.scheduled_start)}–{formatTime(
                          row.scheduled_end
                        )}
                      </span>
                    </div>

                    <div>
                      <span className="block text-slate-400">
                        Location
                      </span>
                      <span className="font-medium">
                        {formatAge(row.heartbeat_age_seconds)}
                      </span>
                    </div>
                  </div>

                  {row.service_type && (
                    <p className="text-xs font-medium text-emerald-700">
                      {row.service_type}
                    </p>
                  )}

                  {row.address && (
                    <div className="flex items-start gap-2 text-xs text-slate-500">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{row.address}</span>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </aside>

        <div className="relative min-h-[500px] xl:min-h-[650px]">
          <MapContainer
            centre={DEFAULT_CENTRE}
            center={DEFAULT_CENTRE}
            zoom={10}
            className="h-full min-h-[500px] w-full xl:min-h-[650px]"
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <FitToMarkers rows={validMapRows} />

            {validMapRows.map((row) => (
              <Marker
                key={`marker-${row.user_id}-${row.visit_id || "none"}`}
                position={[
                  Number(row.latitude),
                  Number(row.longitude),
                ]}
                icon={createMarkerIcon(
                  row.operational_status,
                  row.staff_name
                )}
              >
                <Popup minWidth={290}>
                  <div className="space-y-3">
                    <div>
                      <p className="text-base font-bold text-slate-900">
                        {row.staff_name}
                      </p>

                      <p className="text-sm text-slate-600">
                        {row.client_name}
                      </p>
                    </div>

                    <StatusBadge
                      status={row.operational_status}
                      label={row.status_label}
                    />

                    <div className="space-y-1 text-sm">
                      <p>
                        <strong>Roster:</strong>{" "}
                        {formatTime(row.scheduled_start)}–{formatTime(
                          row.scheduled_end
                        )}
                      </p>

                      {row.service_type && (
                        <p>
                          <strong>Service:</strong> {row.service_type}
                        </p>
                      )}

                      {row.address && (
                        <p>
                          <strong>Address:</strong> {row.address}
                        </p>
                      )}

                      <p>
                        <strong>GPS accuracy:</strong>{" "}
                        {row.accuracy
                          ? `${Math.round(Number(row.accuracy))} m`
                          : "Unknown"}
                      </p>

                      <p>
                        <strong>Last location:</strong>{" "}
                        {formatAge(row.heartbeat_age_seconds)}
                      </p>

                      {row.active_clock?.clock_on_time && (
                        <p>
                          <strong>Clocked on:</strong>{" "}
                          {formatTime(row.active_clock.clock_on_time)}
                        </p>
                      )}
                    </div>

                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${row.latitude},${row.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700"
                    >
                      <Navigation className="h-4 w-4" />
                      Open location
                    </a>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </section>
    </div>
  );
}
